import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, LoaderCircle, ShieldAlert, Signpost } from "lucide-react";
import { useMarketLocation } from "./MarketLocationContext";
import { customStreetSelection, datasetStreetSelection, searchStreets } from "./streetSearch";
import type { StreetReference, StreetSelection, StreetSource } from "./types";

type StreetAutocompleteProps = {
  value: string;
  municipalityId?: string;
  streetId?: string;
  streetSource?: StreetSource;
  disabled?: boolean;
  onChange: (selection: StreetSelection) => void;
};

export function StreetAutocomplete({
  value,
  municipalityId,
  streetId,
  streetSource,
  disabled = false,
  onChange
}: StreetAutocompleteProps) {
  const inputId = useId();
  const listId = `${inputId}-options`;
  const inputRef = useRef<HTMLInputElement>(null);
  const comboboxRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [streets, setStreets] = useState<StreetReference[]>([]);
  const [offlineWithoutCache, setOfflineWithoutCache] = useState(false);
  const { loadStreets } = useMarketLocation();

  useEffect(() => {
    if (!isFocused) setDisplayValue(value);
  }, [isFocused, value]);

  useEffect(() => {
    let active = true;
    setStreets([]);
    setOfflineWithoutCache(false);
    if (!municipalityId) {
      setIsLoading(false);
      return () => { active = false; };
    }

    setIsLoading(true);
    void loadStreets(municipalityId).then((result) => {
      if (!active) return;
      setStreets(result.streets);
      setOfflineWithoutCache(result.offline && result.source === "none");
      setIsLoading(false);
    });
    return () => { active = false; };
  }, [loadStreets, municipalityId]);

  const suggestions = useMemo(() => searchStreets(streets, displayValue), [displayValue, streets]);
  const isOpen = isFocused && !disabled && Boolean(municipalityId) && displayValue.trim().length > 0 && suggestions.length > 0;

  const selectStreet = (street: StreetReference) => {
    const selection = datasetStreetSelection(street);
    setDisplayValue(selection.street);
    onChange(selection);
    setIsFocused(false);
    inputRef.current?.blur();
  };

  return (
    <label className="market-field market-street-field" htmlFor={inputId}>
      <span>Ulica</span>
      <div className="market-location-combobox" ref={comboboxRef}>
        <Signpost className="market-location-icon" size={16} aria-hidden="true" />
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={displayValue}
          disabled={disabled}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={isOpen}
          onFocus={() => setIsFocused(true)}
          onBlur={(event) => {
            if (!comboboxRef.current?.contains(event.relatedTarget)) setIsFocused(false);
          }}
          onChange={(event) => {
            const nextValue = event.target.value;
            setDisplayValue(nextValue);
            onChange(customStreetSelection(nextValue));
          }}
        />

        {isLoading ? <LoaderCircle className="market-location-loader" size={15} aria-label="Načítavam ulice" /> : null}
        {isOpen ? (
          <div className="market-location-options" id={listId} role="listbox" aria-label="Návrhy ulíc">
            {suggestions.map((street) => (
              <button
                key={street.id}
                type="button"
                role="option"
                aria-selected={streetSource === "dataset" && streetId === street.id}
                onPointerDown={(event) => event.preventDefault()}
                onClick={() => selectStreet(street)}
              >
                <strong>{street.name}</strong>
                <Check size={15} aria-hidden="true" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {!municipalityId && !disabled ? (
        <small className="market-location-status custom">Najprv vyberte mesto pre návrhy ulíc.</small>
      ) : null}
      {offlineWithoutCache ? (
        <small className="market-location-status custom"><ShieldAlert size={13} aria-hidden="true" /> Offline · ulicu môžete zadať ručne</small>
      ) : null}
      {displayValue.trim() && streetSource === "custom" ? (
        <small className="market-location-status custom"><ShieldAlert size={13} aria-hidden="true" /> Vlastná hodnota · neoverené</small>
      ) : null}
      {displayValue.trim() && streetSource === "dataset" ? (
        <small className="market-location-status"><Check size={13} aria-hidden="true" /> Štandardizovaný názov</small>
      ) : null}
    </label>
  );
}
